import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLingui } from "@lingui/react/macro";
import { t, msg } from "@lingui/macro";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { TariffRate, Service } from "@/types/billing";
import { chfToRappen, rappenToCHF } from "@/lib/money";
import { timeToMinutes } from "@/lib/time";
import { PatternType } from "./usePricingPatternDetection";
import { useBillingSettings } from "@/hooks/useBilling";

interface TariffSmartPatternViewProps {
  selectedPattern: PatternType;
  rates: TariffRate[];
  // Edited rates come from the unified grid and include filter context.
  // Maintain backward compatibility with legacy number-only maps used by tests.
  editedRates?: Map<string, { value: number; filters: string } | number>;
  onApplyPattern: (
    pattern: PatternType,
    rates: TariffRate[]
  ) => void | Promise<void>;
  isLoading?: boolean;
  selectedAgeTypes?: ("BABY" | "CHILD")[];
  selectedSubsidyTypes?: ("STANDARD" | "SUBSIDISED")[];
  selectedExtraDayTypes?: ("BOOKED" | "EXTRA")[];
  // Legacy props for backward compatibility
  ageType?: "BABY" | "CHILD";
  showSubsidised?: boolean;
  services?: Service[];
  // New props for managing changes without auto-save
  onPendingChanges?: (hasChanges: boolean) => void;
  // Allows parent to request the computed payload synchronously when saving
  onApplyRequest?: (applyFn: () => TariffRate[]) => void;
}

export function TariffSmartPatternView({
  selectedPattern,
  rates,
  editedRates,
  isLoading = false,
  selectedAgeTypes,
  selectedSubsidyTypes,
  selectedExtraDayTypes,
  // Legacy props
  ageType,
  showSubsidised,
  services: propsServices,
  onPendingChanges,
  onApplyRequest,
}: TariffSmartPatternViewProps) {
  const { i18n } = useLingui();
  const [patternValues, setPatternValues] = useState<Record<string, number>>(
    {}
  );
  const [originalValues, setOriginalValues] = useState<
    Record<string, string | number>
  >({});
  // Refs to avoid effect/setState feedback loops when registering apply function with parent
  const patternValuesRef = useRef(patternValues);
  const ageTypesRef = useRef<("BABY" | "CHILD")[]>([]);
  const subsidyTypesRef = useRef<("STANDARD" | "SUBSIDISED")[]>([]);
  const extraDayTypesRef = useRef<("BOOKED" | "EXTRA")[]>([]);
  const selectedPatternRef = useRef<PatternType>(selectedPattern);
  const serviceCodesRef = useRef<string[]>([]);

  // Get services from hook if not provided via props
  const { data: billingSettings } = useBillingSettings();
  const services = useMemo(
    () => propsServices || billingSettings?.services || [],
    [propsServices, billingSettings?.services]
  );

  // Get active services sorted by time (time_from, then time_to)
  const activeServices = useMemo(
    () =>
      services
        .filter((s) => s.active !== false)
        .sort((a, b) => {
          // Sort by time_from first, then by time_to
          const aFromMinutes = timeToMinutes(a.time_from || '00:00');
          const bFromMinutes = timeToMinutes(b.time_from || '00:00');

          if (aFromMinutes !== bFromMinutes) {
            return aFromMinutes - bFromMinutes;
          }

          // If time_from is the same, sort by time_to (descending - longer services first)
          const aToMinutes = timeToMinutes(a.time_to || '23:59');
          const bToMinutes = timeToMinutes(b.time_to || '23:59');
          return bToMinutes - aToMinutes;
        }),
    [services]
  );
  const SERVICE_CODES = activeServices.map((s) => s.code);

  // Handle backward compatibility with memoization
  const ageTypes = useMemo<("BABY" | "CHILD")[]>(
    () => selectedAgeTypes || (ageType ? [ageType] : ["BABY"]),
    [selectedAgeTypes, ageType]
  );

  const subsidyTypes = useMemo<("STANDARD" | "SUBSIDISED")[]>(
    () =>
      selectedSubsidyTypes ||
      (showSubsidised !== undefined
        ? [showSubsidised ? "SUBSIDISED" : "STANDARD"]
        : ["STANDARD"]),
    [selectedSubsidyTypes, showSubsidised]
  );

  const extraDayTypes = useMemo<("BOOKED" | "EXTRA")[]>(
    () => selectedExtraDayTypes || ["BOOKED"],
    [selectedExtraDayTypes]
  );

  // Keep refs updated with latest values
  useEffect(() => {
    patternValuesRef.current = patternValues;
  }, [patternValues]);
  useEffect(() => {
    ageTypesRef.current = ageTypes;
  }, [ageTypes]);
  useEffect(() => {
    subsidyTypesRef.current = subsidyTypes;
  }, [subsidyTypes]);
  useEffect(() => {
    extraDayTypesRef.current = extraDayTypes;
  }, [extraDayTypes]);
  useEffect(() => {
    selectedPatternRef.current = selectedPattern;
  }, [selectedPattern]);
  useEffect(() => {
    serviceCodesRef.current = SERVICE_CODES;
  }, [SERVICE_CODES]);

  // Calculate display values from rates based on pattern
  const calculatedValues = useMemo(() => {
    const values: Record<string, string | number> = {};

    // Helper function to get all rate values for selected filter combinations
    const getRateValues = (
      serviceCode: string,
      dayOfWeek: number
    ): number[] => {
      const values: Map<string, number> = new Map();
      // Build the same filter key format used by the grid component
      const currentFilterKey = `${ageTypes.join(",")}_${subsidyTypes.join(
        ","
      )}_${extraDayTypes.join(",")}`;

      // Check edited rates first
      if (editedRates) {
        const key = `${serviceCode}-${dayOfWeek}`;
        if (editedRates.has(key)) {
          const rawEdited = editedRates.get(key)!;
          // Backward compatibility: number-only map
          if (typeof rawEdited === "number") {
            const editedValue = rawEdited;
            ageTypes.forEach((ageType) => {
              subsidyTypes.forEach((subsidyType) => {
                extraDayTypes.forEach((extraDayType) => {
                  const comboKey = `${ageType}-${subsidyType}-${extraDayType}`;
                  values.set(comboKey, editedValue);
                });
              });
            });
            return Array.from(values.values());
          }

          // New canonical shape with filters
          if (rawEdited && typeof rawEdited === "object") {
            // Only use edited value if it matches the current filter selection
            if (rawEdited.filters === currentFilterKey) {
              const editedValue = rawEdited.value;
              ageTypes.forEach((ageType) => {
                subsidyTypes.forEach((subsidyType) => {
                  extraDayTypes.forEach((extraDayType) => {
                    const comboKey = `${ageType}-${subsidyType}-${extraDayType}`;
                    values.set(comboKey, editedValue);
                  });
                });
              });
              return Array.from(values.values());
            }
          }
        }
      }

      // First, collect all actual rates from data
      const bookedRates: Map<string, number> = new Map();
      const extraRates: Map<string, number> = new Map();

      rates.forEach((rate) => {
        if (rate.service_code !== serviceCode || rate.day_of_week !== dayOfWeek)
          return;

        const ageMatches = ageTypes.includes(rate.age_type as "BABY" | "CHILD");
        const subsidyType = rate.subsidised ? "SUBSIDISED" : "STANDARD";
        const subsidyMatches = subsidyTypes.includes(subsidyType);

        if (ageMatches && subsidyMatches) {
          const baseKey = `${rate.age_type}-${subsidyType}`;
          if (rate.is_extra_day) {
            extraRates.set(baseKey, rate.daily_rate || 0);
          } else {
            bookedRates.set(baseKey, rate.daily_rate || 0);
          }
        }
      });

      // Now build values based on what was requested
      ageTypes.forEach((ageType) => {
        subsidyTypes.forEach((subsidyType) => {
          const baseKey = `${ageType}-${subsidyType}`;
          const bookedRate = bookedRates.get(baseKey);
          const extraRate = extraRates.get(baseKey);

          extraDayTypes.forEach((extraDayType) => {
            const comboKey = `${ageType}-${subsidyType}-${extraDayType}`;

            if (extraDayType === "BOOKED") {
              // Use booked rate if available, otherwise use extra rate as fallback, otherwise 0
              values.set(comboKey, bookedRate ?? extraRate ?? 0);
            } else if (extraDayType === "EXTRA") {
              // Use extra rate if available, otherwise use booked rate as fallback, otherwise 0
              values.set(comboKey, extraRate ?? bookedRate ?? 0);
            }
          });
        });
      });

      return Array.from(values.values());
    };

    if (selectedPattern === "UNIFORM") {
      // For uniform pattern, check if all 7 days have the same rate for each service
      SERVICE_CODES.forEach((serviceCode) => {
        const allRates: number[] = [];
        for (let day = 1; day <= 7; day++) {
          allRates.push(...getRateValues(serviceCode, day));
        }

        // Check if all rates are the same across all filter combinations
        const uniqueRates = [...new Set(allRates)];
        values[serviceCode] =
          uniqueRates.length === 1 ? rappenToCHF(uniqueRates[0]) : "mix";
      });
    } else if (selectedPattern === "WEEKDAY_WEEKEND") {
      // For weekday/weekend pattern, check Mon-Fri and Sat-Sun separately
      SERVICE_CODES.forEach((serviceCode) => {
        // Check weekdays (Mon-Fri, days 1-5)
        const weekdayRates: number[] = [];
        for (let day = 1; day <= 5; day++) {
          weekdayRates.push(...getRateValues(serviceCode, day));
        }

        const uniqueWeekdayRates = [...new Set(weekdayRates)];
        values[`${serviceCode}_weekday`] =
          uniqueWeekdayRates.length === 1
            ? rappenToCHF(uniqueWeekdayRates[0])
            : "mix";

        // Check weekends (Sat-Sun, days 6-7)
        const weekendRates: number[] = [];
        for (let day = 6; day <= 7; day++) {
          weekendRates.push(...getRateValues(serviceCode, day));
        }

        const uniqueWeekendRates = [...new Set(weekendRates)];
        values[`${serviceCode}_weekend`] =
          uniqueWeekendRates.length === 1
            ? rappenToCHF(uniqueWeekendRates[0])
            : "mix";
      });
    }

    return values;
  }, [
    rates,
    selectedPattern,
    ageTypes,
    subsidyTypes,
    extraDayTypes,
    editedRates,
    SERVICE_CODES,
  ]);

  // Check if a field has been edited (different from its original value)
  const isFieldEdited = useMemo(() => {
    const edited: Record<string, boolean> = {};

    // For local pattern values, check against original values
    Object.keys(patternValues).forEach((key) => {
      const currentValue = patternValues[key];
      const originalValue = originalValues[key] ?? calculatedValues[key];
      // Field is edited if it has a value and it's different from original
      // or if original was 'mix' (can't revert to mix)
      edited[key] =
        originalValue === "mix" || currentValue !== Number(originalValue);
    });

    // Also check underlying edited rates if provided
    if (editedRates) {
      if (selectedPattern === "UNIFORM") {
        // For uniform pattern, check if ANY of the 7 days for a service has been edited
        SERVICE_CODES.forEach((serviceCode) => {
          // Only mark as edited if we don't have a local pattern value overriding it
          if (!(serviceCode in patternValues)) {
            let hasEdit = false;
            for (let day = 1; day <= 7; day++) {
              const key = `${serviceCode}-${day}`;
              if (editedRates.has(key)) {
                hasEdit = true;
                break;
              }
            }
            if (hasEdit) {
              edited[serviceCode] = true;
            }
          }
        });
      } else if (selectedPattern === "WEEKDAY_WEEKEND") {
        // For weekday/weekend pattern, check weekdays and weekends separately
        SERVICE_CODES.forEach((serviceCode) => {
          const weekdayKey = `${serviceCode}_weekday`;
          const weekendKey = `${serviceCode}_weekend`;

          // Check if any weekday (Mon-Fri) has been edited
          if (!(weekdayKey in patternValues)) {
            let hasWeekdayEdit = false;
            for (let day = 1; day <= 5; day++) {
              const key = `${serviceCode}-${day}`;
              if (editedRates.has(key)) {
                hasWeekdayEdit = true;
                break;
              }
            }
            if (hasWeekdayEdit) {
              edited[weekdayKey] = true;
            }
          }

          // Check if any weekend day (Sat-Sun) has been edited
          if (!(weekendKey in patternValues)) {
            let hasWeekendEdit = false;
            for (let day = 6; day <= 7; day++) {
              const key = `${serviceCode}-${day}`;
              if (editedRates.has(key)) {
                hasWeekendEdit = true;
                break;
              }
            }
            if (hasWeekendEdit) {
              edited[weekendKey] = true;
            }
          }
        });
      }
    }

    return edited;
  }, [
    editedRates,
    patternValues,
    originalValues,
    calculatedValues,
    selectedPattern,
    SERVICE_CODES,
  ]);

  // Reset local changes when pattern changes or when new rates arrive
  useEffect(() => {
    setPatternValues({});
    setOriginalValues({});
  }, [selectedPattern, rates]);

  // Notify parent about pending changes
  useEffect(() => {
    if (onPendingChanges) {
      const hasChanges = Object.keys(patternValues).length > 0;
      onPendingChanges(hasChanges);
    }
  }, [patternValues, onPendingChanges]);

  // Stable payload computation using refs to avoid re-register loops
  const computePayloadFromRefs = useCallback((): TariffRate[] => {
    const valuesToApply = { ...patternValuesRef.current };
    const currentPattern = selectedPatternRef.current;
    const scodes = serviceCodesRef.current;
    const ages = ageTypesRef.current;
    const subs = subsidyTypesRef.current;
    const extras = extraDayTypesRef.current;

    const newRates: TariffRate[] = [];
    if (Object.keys(valuesToApply).length === 0) return newRates;

    if (currentPattern === "UNIFORM") {
      scodes.forEach((serviceCode) => {
        if (valuesToApply[serviceCode] === undefined) return;
        const rateChf = valuesToApply[serviceCode];
        ages.forEach((ageType) => {
          subs.forEach((subsidyType) => {
            extras.forEach((extraDayType) => {
              for (let day = 1; day <= 7; day++) {
                newRates.push({
                  service_code: serviceCode,
                  day_of_week: day,
                  age_type: ageType,
                  subsidised: subsidyType === "SUBSIDISED",
                  is_extra_day: extraDayType === "EXTRA",
                  daily_rate: chfToRappen(rateChf),
                } as TariffRate);
              }
            });
          });
        });
      });
    } else {
      scodes.forEach((serviceCode) => {
        const weekdayKey = `${serviceCode}_weekday`;
        const weekendKey = `${serviceCode}_weekend`;
        const weekdayEdited = weekdayKey in valuesToApply;
        const weekendEdited = weekendKey in valuesToApply;
        if (!weekdayEdited && !weekendEdited) return;

        ages.forEach((ageType) => {
          subs.forEach((subsidyType) => {
            extras.forEach((extraDayType) => {
              for (let day = 1; day <= 7; day++) {
                if (day <= 5 && weekdayEdited) {
                  newRates.push({
                    service_code: serviceCode,
                    day_of_week: day,
                    age_type: ageType,
                    subsidised: subsidyType === "SUBSIDISED",
                    is_extra_day: extraDayType === "EXTRA",
                    daily_rate: chfToRappen(valuesToApply[weekdayKey]),
                  } as TariffRate);
                } else if (day >= 6 && weekendEdited) {
                  newRates.push({
                    service_code: serviceCode,
                    day_of_week: day,
                    age_type: ageType,
                    subsidised: subsidyType === "SUBSIDISED",
                    is_extra_day: extraDayType === "EXTRA",
                    daily_rate: chfToRappen(valuesToApply[weekendKey]),
                  } as TariffRate);
                }
              }
            });
          });
        });
      });
    }
    return newRates;
  }, []);

  // Register once; the function uses refs to read latest values
  useEffect(() => {
    if (!onApplyRequest) return;
    onApplyRequest(() => computePayloadFromRefs());
  }, [onApplyRequest, computePayloadFromRefs]);

  const getServiceTimes = (serviceCode: string): string => {
    const service = services.find((s) => s.code === serviceCode);
    if (service?.time_from && service?.time_to) {
      return `${service.time_from}-${service.time_to}`;
    }
    return "";
  };

  const getServiceDisplay = (code: string) => {
    const service = services.find((s) => s.code === code);
    let name = code; // Default fallback to code

    if (service) {
      // Get localized name based on current locale
      const locale = i18n.locale;
      const localizedNameKey = `name_${locale}` as keyof typeof service;
      const localizedName = service[localizedNameKey] as string | undefined;

      // Priority: localized name > default name > code
      name = localizedName || service.name || code;
    }

    const times = getServiceTimes(code);
    return { name, times };
  };

  // Get the display value for an input field
  const getDisplayValue = (key: string): string => {
    // If user has edited this field, show the edited value
    if (key in patternValues) {
      return patternValues[key].toString();
    }

    // Otherwise show the calculated value or "mix"
    const calculated = calculatedValues[key];
    if (calculated === "mix") {
      return ""; // Show empty for "mix" to allow typing
    }
    return calculated !== undefined ? calculated.toString() : "";
  };

  // Get the placeholder text for an input field
  const getPlaceholder = (key: string): string => {
    // If not edited and calculated value is "mix", show "mix" as placeholder
    if (!(key in patternValues) && calculatedValues[key] === "mix") {
      return i18n._(t`mix`);
    }
    return "0.00";
  };

  const handleValueChange = (key: string, value: string) => {
    // Store original value on first edit
    if (!(key in originalValues) && !(key in patternValues)) {
      const originalValue = calculatedValues[key];
      if (originalValue !== undefined) {
        setOriginalValues((prev) => ({
          ...prev,
          [key]: originalValue,
        }));
      }
    }

    const numValue = parseFloat(value) || 0;
    const originalValue = originalValues[key] ?? calculatedValues[key];

    // Update local pattern values
    const updatedPatternValues = { ...patternValues };
    // If value matches original and original wasn't 'mix', remove the edit
    if (originalValue !== "mix" && numValue === Number(originalValue)) {
      delete updatedPatternValues[key];
    } else {
      updatedPatternValues[key] = numValue;
    }
    setPatternValues(updatedPatternValues);

    // DO NOT apply changes to parent - let parent handle save/reset explicitly
  };

  // This section is now moved up earlier in the file, before triggerApply

  return (
    <div className="space-y-4">
      {/* Table layout for both patterns */}
      <Table className="table-fixed">
        {/* Percentage layout: Service 25%, the rest 75% */}
        <colgroup>
          <col style={{ width: "25%" }} />
          {selectedPattern === "UNIFORM" ? (
            <col style={{ width: "75%" }} />
          ) : (
            <>
              <col style={{ width: "37.5%" }} />
              <col style={{ width: "37.5%" }} />
            </>
          )}
        </colgroup>
        <TableHeader>
          <TableRow>
            <TableHead>{i18n._(t`Service`)}</TableHead>
            {selectedPattern === "UNIFORM" ? (
              <TableHead className="text-center">
                {i18n._(t`All Days`)}
              </TableHead>
            ) : (
              <>
                <TableHead className="text-center">
                  {i18n._(t`Weekday (Mon-Fri)`)}
                </TableHead>
                <TableHead className="text-center">
                  {i18n._(t`Weekend (Sat-Sun)`)}
                </TableHead>
              </>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {SERVICE_CODES.map((serviceCode) => {
            const { name, times } = getServiceDisplay(serviceCode);
            return (
              <TableRow key={serviceCode}>
                <TableCell className="font-medium">
                  <div className="flex items-center justify-between w-full min-w-0">
                    <span className="min-w-0 truncate">{name}</span>
                    {times && (
                      <span className="text-xs font-normal text-muted-foreground whitespace-nowrap ml-2 shrink-0">
                        {times}
                      </span>
                    )}
                  </div>
                </TableCell>
                {selectedPattern === "UNIFORM" ? (
                  <TableCell className="p-1">
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={getDisplayValue(serviceCode)}
                      onChange={(e) =>
                        handleValueChange(serviceCode, e.target.value)
                      }
                      placeholder={getPlaceholder(serviceCode)}
                      className={`text-center text-sm ${
                        isFieldEdited[serviceCode] ||
                        serviceCode in patternValues
                          ? "tariff-field-edited"
                          : ""
                      }`}
                      disabled={isLoading}
                      aria-label={i18n._(
                        msg({
                          id: "tariffs.smartPattern.priceFor",
                          message: "Price for {service}",
                        }),
                        { service: getServiceDisplay(serviceCode).name }
                      )}
                    />
                  </TableCell>
                ) : (
                  <>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={getDisplayValue(`${serviceCode}_weekday`)}
                        onChange={(e) =>
                          handleValueChange(
                            `${serviceCode}_weekday`,
                            e.target.value
                          )
                        }
                        placeholder={getPlaceholder(`${serviceCode}_weekday`)}
                        className={`text-center text-sm ${
                          isFieldEdited[`${serviceCode}_weekday`] ||
                          `${serviceCode}_weekday` in patternValues
                            ? "tariff-field-edited"
                            : ""
                        }`}
                        disabled={isLoading}
                        aria-label={i18n._(
                          msg({
                            id: "tariffs.smartPattern.weekdayPriceFor",
                            message: "Weekday price for {service}",
                          }),
                          { service: getServiceDisplay(serviceCode).name }
                        )}
                      />
                    </TableCell>
                    <TableCell className="p-1">
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={getDisplayValue(`${serviceCode}_weekend`)}
                        onChange={(e) =>
                          handleValueChange(
                            `${serviceCode}_weekend`,
                            e.target.value
                          )
                        }
                        placeholder={getPlaceholder(`${serviceCode}_weekend`)}
                        className={`text-center text-sm ${
                          isFieldEdited[`${serviceCode}_weekend`] ||
                          `${serviceCode}_weekend` in patternValues
                            ? "tariff-field-edited"
                            : ""
                        }`}
                        disabled={isLoading}
                        aria-label={i18n._(
                          msg({
                            id: "tariffs.smartPattern.weekendPriceFor",
                            message: "Weekend price for {service}",
                          }),
                          { service: getServiceDisplay(serviceCode).name }
                        )}
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
